import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import HeroSection from './components/HeroSection';
import TechnologyShowcase from './components/TechnologyShowcase';
import WhyBytePairs from './components/WhyBytePairs';
import PerformanceMetrics from './components/PerformanceMetrics';
import ClientSuccessStories from './components/ClientSuccessStories';
import FloatingAIAssistant from './components/FloatingAIAssistant';
import { useModal } from '../../contexts/ModalContext';

const Homepage = () => {
  const { openConsultation } = useModal();

  const handleScheduleClick = () => {
    const el = document.getElementById('consultation-form');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Byte Pairs | Custom Software & Web Development Company in Dharapuram</title>
        <meta name="description" content="Byte Pairs builds high-performance custom billing software, business CRMs, mobile apps, and WhatsApp automations in Dharapuram, Tamil Nadu. Pay once, own the code." />
        <meta name="keywords" content="software company in Dharapuram, IT company in Dharapuram, software development company in Tamil Nadu, website development company, CRM development company, AI development company, BytePairs, Byte Pairs, custom software development, Dharapuram, Tamil Nadu, India" />
        <meta property="og:title" content="Byte Pairs | Custom Software & Web Development Company in Dharapuram" />
        <meta property="og:description" content="High-performance custom software, billing systems, mobile apps, and automated workflows. Pay once, own the code forever." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bytepairs.com/" />
        <meta property="og:image" content="https://bytepairs.com/android-chrome-512x512.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Byte Pairs | Custom Software & Web Development" />
        <meta name="twitter:description" content="Custom software, websites & mobile apps for growing businesses in India. Direct developer access, honest pricing." />
        <meta name="twitter:image" content="https://bytepairs.com/android-chrome-512x512.png" />
        <link rel="canonical" href="https://bytepairs.com/" />

        {/* WebSite Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Byte Pairs",
            "url": "https://bytepairs.com/",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://bytepairs.com/?s={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>

        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Byte Pairs",
            "url": "https://bytepairs.com/",
            "logo": "https://bytepairs.com/assets/bytepairs-logo.png",
            "description": "Enterprise-grade custom software development, mobile app design, business automations, and search engine optimization services.",
            "email": "bytepairs2026@gmail.com",
            "telephone": "+916374241548",
            "sameAs": [
              "https://linkedin.com/company/bytepairs",
              "https://twitter.com/bytepairs",
              "https://github.com/bytepairs"
            ]
          })}
        </script>

        {/* Local Business (Professional Service) Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Byte Pairs",
            "image": "https://bytepairs.com/android-chrome-512x512.png",
            "url": "https://bytepairs.com/",
            "telephone": "+916374241548",
            "email": "bytepairs2026@gmail.com",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Dharapuram",
              "addressLocality": "Dharapuram",
              "addressRegion": "Tamil Nadu",
              "postalCode": "638656",
              "addressCountry": "IN"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "10.7323",
              "longitude": "77.5256"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday"
              ],
              "opens": "09:00",
              "closes": "18:00"
            },
            "areaServed": [
              {
                "@type": "AdministrativeArea",
                "name": "Dharapuram",
                "sameAs": [
                  "https://en.wikipedia.org/wiki/Dharapuram",
                  "https://www.wikidata.org/wiki/Q1259508"
                ]
              },
              {
                "@type": "AdministrativeArea",
                "name": "Tamil Nadu",
                "sameAs": [
                  "https://en.wikipedia.org/wiki/Tamil_Nadu",
                  "https://www.wikidata.org/wiki/Q1445"
                ]
              },
              {
                "@type": "AdministrativeArea",
                "name": "India",
                "sameAs": [
                  "https://en.wikipedia.org/wiki/India",
                  "https://www.wikidata.org/wiki/Q668"
                ]
              }
            ]
          })}
        </script>

        {/* WebPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Byte Pairs | Custom Software & Web Development Company in Dharapuram",
            "description": "Byte Pairs builds custom software, web portals, mobile apps, and business automations for small businesses in India. Honest development, transparent pricing.",
            "url": "https://bytepairs.com/"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <Header />

        {/* Main Content */}
        <main className="pt-16">
          {/* Hero Section */}
          <HeroSection />

          {/* Technology Showcase */}
          <TechnologyShowcase />

          {/* Why Choose Us */}
          <WhyBytePairs />

          {/* Client Success Stories & Real Projects */}
          <ClientSuccessStories />

          {/* Final CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-accent/5 to-success/5">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
                  Ready to Build Something Amazing?
                </h2>
                <p className="text-xl text-text-secondary mb-8 max-w-2xl mx-auto">
                  Let's transform your vision into reality with cutting-edge technology and expert craftsmanship.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                  <button
                    onClick={openConsultation}
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-xl font-medium hover:shadow-brand-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <Icon name="Rocket" size={20} />
                    <span>Start Your Project</span>
                  </button>
                  <button
                    onClick={handleScheduleClick}
                    className="inline-flex items-center space-x-2 bg-card border border-border text-foreground px-8 py-4 rounded-xl font-medium hover:bg-muted transition-all duration-300"
                  >
                    <Icon name="Calendar" size={20} />
                    <span>Schedule Consultation</span>
                  </button>
                </div>
                <div className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Get in Touch</h3>
                  <div className="flex flex-col md:flex-row gap-6 justify-center items-center text-text-secondary">
                    <div className="flex items-center space-x-2">
                      <Icon name="Mail" size={18} />
                      <span>bytepairs2026@gmail.com</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Phone" size={18} />
                      <span>+91 6374241548</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Floating AI Assistant */}
          <FloatingAIAssistant />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Homepage;